/**
 * OtaManagerPage — Sprint 6.4 (Fleet OTA Manager).
 *
 * Aba "OTA / Firmware" do NOC. Controla atualizações OTA em massa
 * com segurança, reutilizando o motor de rollout progressivo.
 *
 *  - Cards: versão atual · releases · rollouts ativos · falhas ·
 *    devices desatualizados.
 *  - Lista de rollouts com progresso agregado.
 *  - Criar rollout: release → waves (canário) → confirmar.
 *  - Detalhe: waves + progresso; ações pause/resume/cancel/retry/
 *    rollback.
 *
 * Segurança na UI: a primeira wave (canário) é limitada a 10%; o
 * rollout que atinge 100% exige confirmação explícita.
 *
 * Polling de 15s.
 */
import { useCallback, useEffect, useState } from "react";
import {
  createOtaRollout,
  getOtaOverview,
  getOtaRollout,
  getOtaRolloutDevices,
  listOtaRollouts,
  listFirmwareReleases,
  otaRolloutAction,
} from "../lib/api.js";
import { useCriticalConfirm } from "../components/ConfirmCriticalAction.jsx";
import { formatRelative } from "../lib/format.js";
import Can from "../components/Can.jsx";
import { CAP } from "../lib/rbac.js";

const POLL_MS = 15000;
// Salvaguarda — espelha CANARY_MAX_PERCENT do backend.
const CANARY_MAX = 10;

const STATUS_STYLE = {
  active: "text-noc-ok",
  paused: "text-noc-warn",
  completed: "text-noc-accent",
  cancelled: "text-noc-muted",
  rolled_back: "text-noc-purple",
  failed: "text-noc-err",
};

export default function OtaManagerPage() {
  const [overview, setOverview] = useState(null);
  const [rollouts, setRollouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async (signal) => {
    try {
      const [ov, rl] = await Promise.all([
        getOtaOverview({ signal }),
        listOtaRollouts({ signal }),
      ]);
      setOverview(ov);
      setRollouts(rl.rollouts || []);
      setErr(null);
    } catch (e) {
      if (e?.name !== "AbortError") {
        setErr(e?.message || "Falha ao carregar dados de OTA.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    const id = setInterval(() => load(), POLL_MS);
    return () => { ac.abort(); clearInterval(id); };
  }, [load]);

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <h1 className="text-2xl font-bold tracking-tight text-noc-text">
          OTA / Firmware
        </h1>
        <Can capability={CAP.DEVICES_OTA}>
        <button
          onClick={() => setShowCreate(true)}
          className="text-xs px-2.5 py-1 rounded border border-noc-accent/40 bg-noc-accent/15 text-noc-accent font-medium"
        >
          ＋ Novo rollout
        </button>
        </Can>
      </div>

      {err && (
        <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded-lg p-2.5">
          {err}
        </div>
      )}

      <OverviewCards overview={overview} loading={loading} />

      <RolloutList
        rollouts={rollouts}
        loading={loading}
        onOpen={(id) => setDetailId(id)}
      />

      {showCreate && (
        <CreateRolloutModal
          latestRelease={overview?.latest_release}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}

      {detailId && (
        <RolloutDetailModal
          batchId={detailId}
          onClose={() => setDetailId(null)}
          onChanged={() => load()}
        />
      )}
    </div>
  );
}

/* ════════ Cards de overview ════════ */
function OverviewCards({ overview, loading }) {
  const o = overview || {};
  const cards = [
    { label: "Versão atual", value: o.current_version || "—",
      tone: "text-noc-text" },
    { label: "Total de devices", value: o.total_devices ?? "—",
      tone: "text-noc-text" },
    { label: "Rollouts ativos", value: o.active_rollouts ?? "—",
      tone: "text-noc-ok" },
    { label: "Desatualizados", value: o.outdated_count ?? "—",
      tone: "text-noc-warn" },
    { label: "Falhas OTA", value: o.failed_commands ?? "—",
      tone: "text-noc-err" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
      {cards.map((c) => (
        <div key={c.label}
             className="glass-card-pad">
          <div className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-noc-faint">
            {c.label}
          </div>
          <div className={"font-mono text-lg font-bold " + c.tone}>
            {loading ? "…" : c.value}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ════════ Lista de rollouts ════════ */
function RolloutList({ rollouts, loading, onOpen }) {
  if (loading && rollouts.length === 0) {
    return (
      <div className="text-sm font-mono text-[12px] text-noc-faint">
        Carregando rollouts…
      </div>
    );
  }
  if (rollouts.length === 0) {
    return (
      <div className="glass-card-pad text-sm text-noc-muted/70">
        Nenhum rollout registrado. Crie o primeiro com “Novo
        rollout”.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="text-xs font-semibold uppercase tracking-wide text-noc-muted">
        Rollouts
      </div>
      {rollouts.map((r) => (
        <button
          key={r.id}
          onClick={() => onOpen(r.id)}
          className="w-full text-left glass-card-pad hover: border-noc-accent/40 transition-colors"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium">
              {r.release_version || "release ?"}
            </span>
            <span className={
              "text-[10px] px-1.5 py-0.5 rounded-full border "
              + "border-noc-border "
              + (STATUS_STYLE[r.status] || "text-noc-muted")
            }>
              {r.status || "—"}
            </span>
            {r.is_progressive && (
              <span className="text-[10px] text-noc-muted">
                progressivo
              </span>
            )}
            <span className="text-[10px] text-noc-muted ml-auto">
              {formatRelative(r.created_at)}
            </span>
          </div>
          <ProgressBar progress={r.progress} />
        </button>
      ))}
    </div>
  );
}

function ProgressBar({ progress }) {
  const p = progress || { percent: 0, done: 0, failed: 0,
    pending: 0, total: 0 };
  return (
    <div className="mt-1.5">
      <div className="h-2 rounded-full bg-noc-panel2 overflow-hidden flex">
        <div className="h-full bg-noc-ok/10"
             style={{ width: p.percent + "%" }} />
        {p.total > 0 && p.failed > 0 && (
          <div className="h-full bg-noc-err/10"
               style={{
                 width: (p.failed / p.total * 100) + "%",
               }} />
        )}
      </div>
      <div className="flex justify-between text-[10px] text-noc-muted mt-0.5">
        <span>{p.done}/{p.total} concluídos</span>
        {p.failed > 0 && (
          <span className="text-noc-err">{p.failed} falhas</span>
        )}
        <span>{p.percent}%</span>
      </div>
    </div>
  );
}

/* ════════ Modal — criar rollout ════════ */
function CreateRolloutModal({ latestRelease, onClose, onCreated }) {
  const confirmCritical = useCriticalConfirm();  // S3.10C2
  const [releases, setReleases] = useState([]);
  const [releaseId, setReleaseId] = useState(
    latestRelease?.id || ""
  );
  const [wavesText, setWavesText] = useState("5, 25, 100");
  const [confirmProd, setConfirmProd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const ac = new AbortController();
    listFirmwareReleases({ signal: ac.signal })
      .then((d) => setReleases(d.releases || d || []))
      .catch(() => setReleases([]));
    return () => ac.abort();
  }, []);

  // parse das waves + validação local (espelha o backend)
  const waves = wavesText
    .split(",")
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => !Number.isNaN(n));

  const reachesProd = waves.includes(100);
  let localErr = null;
  if (waves.length === 0) {
    localErr = "Informe ao menos uma wave.";
  } else if (waves[0] > CANARY_MAX) {
    localErr = `A 1ª wave é o canário e não pode passar de `
             + `${CANARY_MAX}%. Rollout global direto é bloqueado.`;
  } else if (waves.some((p) => p < 1 || p > 100)) {
    localErr = "Cada wave deve estar entre 1 e 100.";
  } else if (JSON.stringify(waves) !==
             JSON.stringify([...waves].sort((a, b) => a - b))) {
    localErr = "As waves devem ser crescentes.";
  } else if (waves[waves.length - 1] !== 100) {
    localErr = "A última wave deve ser 100%.";
  }

  async function submit() {
    if (localErr) { setErr(localErr); return; }
    if (!releaseId) { setErr("Selecione uma release."); return; }
    const _secret = await confirmCritical({
      actionLabel: "Criar rollout (OTA em massa)",
      target: {},
      impact: "Vai atualizar firmware progressivamente em ondas (canário → 100%).",
    });
    if (_secret == null) return;
    setBusy(true);
    setErr(null);
    try {
      await createOtaRollout({
        release_id: releaseId,
        waves,
        confirm_production: confirmProd,
      }, { confirm: _secret });
      onCreated();
    } catch (e) {
      setErr(e?.message || "Falha ao criar o rollout.");
      setBusy(false);
    }
  }

  return (
    <Modal title="Novo rollout OTA" onClose={onClose}>
      <div className="space-y-3">
        <div>
          <label className="text-xs text-noc-muted block mb-1">
            Release de firmware
          </label>
          <select
            value={releaseId}
            onChange={(e) => setReleaseId(e.target.value)}
            className="w-full bg-noc-panel2 border border-noc-border rounded-lg px-2.5 py-2 text-sm text-noc-text"
          >
            <option value="">— selecione —</option>
            {releases.map((r) => (
              <option key={r.id} value={r.id}>
                {r.version}{r.deprecated ? " (depreciada)" : ""}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-xs text-noc-muted block mb-1">
            Waves (% crescentes, terminando em 100)
          </label>
          <input
            value={wavesText}
            onChange={(e) => setWavesText(e.target.value)}
            placeholder="5, 25, 100"
            className="w-full bg-noc-panel2 border border-noc-border rounded-lg px-2.5 py-2 text-sm text-noc-text"
          />
          <div className="text-[10px] text-noc-muted mt-1">
            A 1ª wave é o canário — no máximo {CANARY_MAX}% da frota.
            O rollout global direto é bloqueado por segurança.
          </div>
        </div>

        {/* alerta antes de produção */}
        {reachesProd && !localErr && (
          <label className="flex items-start gap-2 text-[11px] text-noc-warn bg-noc-warn/10 border border-noc-warn/30 rounded-lg p-2">
            <input
              type="checkbox"
              checked={confirmProd}
              onChange={(e) => setConfirmProd(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              Este rollout atinge 100% da frota (produção).
              Confirmo que validei o canário e quero prosseguir.
            </span>
          </label>
        )}

        {(err || localErr) && (
          <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded-lg p-2">
            {err || localErr}
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button
            onClick={onClose}
            className="text-xs px-3 py-1.5 rounded border border-noc-border text-noc-muted"
          >
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={busy || !!localErr ||
                      (reachesProd && !confirmProd)}
            className="text-xs px-3 py-1.5 rounded font-semibold bg-noc-accent/15 text-noc-accent border border-noc-accent/40 disabled:opacity-50"
          >
            {busy ? "Criando…" : "Abrir rollout"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* ════════ Modal — detalhe do rollout ════════ */
function RolloutDetailModal({ batchId, onClose, onChanged }) {
  const confirmCritical = useCriticalConfirm();  // S3.10C2
  const [detail, setDetail] = useState(null);
  const [devices, setDevices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async (signal) => {
    try {
      const [d, dv] = await Promise.all([
        getOtaRollout(batchId, { signal }),
        getOtaRolloutDevices(batchId, { signal }),
      ]);
      setDetail(d);
      setDevices(dv.devices || []);
    } catch (e) {
      if (e?.name !== "AbortError") {
        setErr(e?.message || "Falha ao carregar o rollout.");
      }
    }
  }, [batchId]);

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    const id = setInterval(() => load(), POLL_MS);
    return () => { ac.abort(); clearInterval(id); };
  }, [load]);

  async function doAction(action, query) {
    // S3.10C2 — retry/rollback empurram firmware: exigem confirmação forte.
    let confirm;
    if (action === "retry" || action === "rollback") {
      const _secret = await confirmCritical({
        actionLabel: action === "rollback" ? "Rollback do rollout" : "Retry de OTA",
        target: {},
        impact: action === "rollback"
          ? "Cria um batch inverso — devices recebem OTA de volta."
          : "Reenfileira OTA nos devices que falharam.",
      });
      if (_secret == null) return;
      confirm = _secret;
    }
    setBusy(true);
    setErr(null);
    try {
      await otaRolloutAction(batchId, action, { query, confirm });
      await load();
      onChanged();
    } catch (e) {
      setErr(e?.message || `Falha na ação "${action}".`);
    } finally {
      setBusy(false);
    }
  }

  const failedCount = devices.filter(
    (d) => d.status === "failed" || d.status === "expired"
  ).length;

  return (
    <Modal title="Rollout" onClose={onClose} wide>
      {!detail && !err && (
        <div className="text-sm font-mono text-[12px] text-noc-faint">
          Carregando…
        </div>
      )}
      {err && (
        <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded-lg p-2 mb-2">
          {err}
        </div>
      )}
      {detail && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={
              "text-xs px-2 py-0.5 rounded-full border "
              + "border-noc-border "
              + (STATUS_STYLE[detail.status] || "text-noc-muted")
            }>
              {detail.status}
            </span>
            <span className="text-[11px] text-noc-muted">
              {detail.enqueued_count} enfileirados ·{" "}
              {detail.skipped_count} pulados
            </span>
          </div>

          <ProgressBar progress={detail.progress} />

          {/* Waves */}
          <div>
            <div className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-noc-faint mb-1">
              Waves
            </div>
            <div className="space-y-1">
              {(detail.waves || []).map((w) => (
                <div key={w.id}
                     className="flex items-center gap-2 text-[11px] glass-card-pad py-1.5">
                  <span className="font-medium">
                    #{w.wave_index} · {w.target_percent}%
                  </span>
                  <span className="text-noc-muted">
                    {w.completed_devices}/{w.planned_devices} ok
                  </span>
                  {w.failed_devices > 0 && (
                    <span className="text-noc-err">
                      {w.failed_devices} falhas
                    </span>
                  )}
                  <span className={
                    "ml-auto " +
                    (STATUS_STYLE[w.status] || "text-noc-muted")
                  }>
                    {w.status}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Ações */}
          <div className="flex flex-wrap gap-2">
            <Can
              capability={CAP.DEVICES_OTA}
              fallback={<span className="text-xs text-noc-muted/60 italic">Somente leitura — sem permissão para ações de OTA.</span>}
            >
            <ActionBtn label="Pausar" disabled={busy}
                       onClick={() => doAction("pause")} />
            <ActionBtn label="Retomar" disabled={busy}
                       onClick={() => doAction("resume")} />
            <ActionBtn label="Cancelar" disabled={busy} danger
                       onClick={() => doAction("cancel")} />
            <ActionBtn
              label={`Retry falhas (${failedCount})`}
              disabled={busy || failedCount === 0}
              onClick={() => doAction("retry")}
            />
            </Can>
          </div>

          {/* Devices */}
          <div>
            <div className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-noc-faint mb-1">
              Devices ({devices.length})
            </div>
            <div className="max-h-48 overflow-y-auto space-y-1">
              {devices.map((d) => (
                <div key={d.command_id}
                     className="flex items-center gap-2 text-[11px] glass-card-pad py-1">
                  <span className="font-mono">{d.device_id}</span>
                  <span className="text-noc-muted">
                    fw {d.current_fw || "?"}
                  </span>
                  <span className={
                    "ml-auto " +
                    (d.status === "acked"
                      ? "text-noc-ok"
                      : d.status === "failed" ||
                        d.status === "expired"
                        ? "text-noc-err"
                        : "text-noc-warn")
                  }>
                    {d.status}
                  </span>
                </div>
              ))}
              {devices.length === 0 && (
                <div className="text-[11px] font-mono text-[12px] text-noc-faint">
                  Sem devices neste rollout.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ════════ Auxiliares ════════ */
function ActionBtn({ label, onClick, disabled, danger }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={
        "text-[11px] px-2.5 py-1 rounded border disabled:opacity-40 "
        + (danger
            ? "border-noc-err/30 text-noc-err bg-noc-err/10"
            : "border-noc-border text-noc-text")
      }
    >
      {label}
    </button>
  );
}

function Modal({ title, children, onClose, wide }) {
  return (
    <div
      className="fixed inset-0 z-40 bg-black/60 flex items-center justify-center p-3"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={
          "bg-noc-panel border border-noc-border rounded-xl "
          + "shadow-xl w-full p-4 max-h-[88vh] overflow-y-auto "
          + (wide ? "max-w-lg" : "max-w-md")
        }
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-noc-text">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="h-7 w-7 rounded-lg text-noc-muted border border-noc-border"
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
